import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Cpu, Gauge, KeyRound, PackageCheck, Plus, RefreshCw, RotateCw, ShieldCheck, SlidersHorizontal, Store, Trash2, XCircle } from "lucide-react";
import { serviceAccountPurposePresets, serviceAccountPurposes, type ServiceAccountPurpose, type SessionView } from "@control-plane/contracts";
import { PageHeader } from "../components/PageHeader";
import { RemotePanel } from "../components/RemotePanel";
import { StatusBadge } from "../components/StatusBadge";
import { Dialog } from "../components/Overlay";
import { V3Select } from "../components/V3Select";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Wizard } from "../components/Wizard";
import { V3PageTabs, type V3PageTab } from "../components/V3PageTabs";
import { api } from "../lib/api";
import { businessLabel } from "../lib/displayNames";
import { appNavigate } from "../lib/navigate";
import { useRemote } from "../lib/useRemote";
import { runtimeCapabilities } from "../lib/pageCapabilities";

type AccountRisk = string | { level?: string; summary?: string; nextStep?: string };
type Account = {
  id: string;
  tenantId: string;
  tenantName?: string | null;
  name?: string | null;
  purpose?: ServiceAccountPurpose | string | null;
  status: string;
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  lastSource?: string | null;
  failedAuthCount?: number | null;
  activeVersion?: number | null;
  fingerprint?: string | null;
  lastRotatedAt?: string | null;
  risk?: AccountRisk | null;
};
type PlanCatalogItem = { id: string; name: string; versions: Array<{ id: string; status: string }> };
type Subscription = { id: string; tenantName: string; planName: string; status: string };
type Meter = { id: string; displayName: string; unit: string; aggregation?: string; source?: string; lifecycle?: string };
type Tenant = { id: string; name: string };
type PrintAgentPolicy = { id: string; policyVersion: number; allowedOrigins: string[]; status: string; updatedAt: string };
type QuotaPolicy = { id: string | null; platforms: Record<string, { qpsLimit: number; dailyCallBudget: number; warningThreshold: number; backoffCapMs: number }>; updatedAt: string | null };
type RuntimeData = { accounts: Account[] | null; planCatalog: PlanCatalogItem[] | null; subscriptions: Subscription[] | null; meters: Meter[]; tenants: Tenant[]; printAgentPolicy: PrintAgentPolicy | null; quotaPolicy: QuotaPolicy | null };
type Feedback = { tone: "success" | "danger"; text: string } | null;
type SecretRevealState = { title: string; description: string; secret: string; fingerprint?: string | null; onSaved?: () => void } | null;
type CreateAccountResponse = Account & { clientSecret: string };
type RotationStartResponse = { clientSecret: string | null; version: number; activeVersion?: number; fingerprint?: string | null; idempotentReplay?: boolean };
type OriginProbeStatus = "reachable" | "failed" | "development_only";
type OriginProbeResult = { origin: string; status: OriginProbeStatus; httpStatus?: number | null; checkedAt?: string | null; errorCode?: string | null };
type OriginProbeResponse = { results: OriginProbeResult[]; allRequiredReachable: boolean };
type OriginProbeState = OriginProbeResponse & { originsKey: string };
type OriginRow = { origin: string; required: boolean; developmentOnly: boolean; probe: OriginProbeResult | null };
type RuntimeTab = "accounts" | "metering" | "print";

const sourceLabels: Record<string, string> = {
  erp_runtime: "ERP 业务运行",
  print_agent: "打印组件",
  integration_worker: "连接检查任务",
  runtime_policy: "运行策略读取",
  usage_report: "用量上报",
  platform_config: "平台配置读取",
};
const riskLabels: Record<string, string> = {
  none: "暂无风险",
  normal: "暂无风险",
  low: "低风险",
  medium: "需关注",
  high: "高风险",
  critical: "立即处理",
  auth_failures: "近期存在验证失败",
  rotation_due: "建议安排密钥轮换",
  expired: "账号已过期",
  revoked: "账号已停用",
};

function FeedbackBar({ feedback }: { feedback: Feedback }) {
  if (!feedback) return null;
  return <div className={`feedback-bar ${feedback.tone}`}>{feedback.tone === "success" ? <CheckCircle2 size={16} /> : <XCircle size={16} />}<span>{feedback.text}</span></div>;
}

export function RuntimePage({ session }: { session: SessionView }) {
  const caps = runtimeCapabilities(session.actions);
  const runtimeTabs: V3PageTab[] = [
    ...(caps.loadServiceAccounts ? [{ value: "accounts", label: "服务账号", icon: KeyRound, panelId: "runtime-panel-accounts" }] : []),
    { value: "metering", label: "配额与计量", icon: Gauge, panelId: "runtime-panel-metering" },
    ...(caps.loadPrintPolicy ? [{ value: "print", label: "打印安全", icon: ShieldCheck, panelId: "runtime-panel-print" }] : []),
  ];
  const loader = useCallback(async (): Promise<RuntimeData> => {
    const [accounts, planCatalog, subscriptions, meters, tenants, printAgentPolicy, quotaPolicy] = await Promise.all([
      caps.loadServiceAccounts ? api<{ items: Account[] }>("/api/service-accounts") : Promise.resolve(null),
      caps.loadPlanCatalog ? api<{ items: PlanCatalogItem[] }>("/api/plan-catalog") : Promise.resolve(null),
      caps.loadSubscriptions ? api<{ items: Subscription[] }>("/api/subscriptions") : Promise.resolve(null),
      api<{ items: Meter[] }>("/api/usage/meters"),
      caps.loadTenants ? api<{ items: Tenant[] }>("/api/tenants") : Promise.resolve({ items: [] as Tenant[] }),
      caps.loadPrintPolicy ? api<{ policy: PrintAgentPolicy | null }>("/api/print-agent-origin-policy") : Promise.resolve(null),
      caps.loadQuotaPolicy ? api<QuotaPolicy>("/api/quota-policy") : Promise.resolve(null),
    ]);
    return { accounts: accounts?.items ?? null, planCatalog: planCatalog?.items ?? null, subscriptions: subscriptions?.items ?? null, meters: meters.items, tenants: tenants.items, printAgentPolicy: printAgentPolicy?.policy ?? null, quotaPolicy };
  }, [caps.loadServiceAccounts, caps.loadPlanCatalog, caps.loadSubscriptions, caps.loadTenants, caps.loadPrintPolicy, caps.loadQuotaPolicy]);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busyAction, setBusyAction] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [rotationAccount, setRotationAccount] = useState<Account | null>(null);
  const [secretReveal, setSecretReveal] = useState<SecretRevealState>(null);
  const [activeTab, setActiveTab] = useState<RuntimeTab>(caps.loadServiceAccounts ? "accounts" : "metering");
  const remote = useRemote(loader);

  async function runAction(action: () => Promise<void>, successText: string) {
    setBusyAction(true); setFeedback(null);
    try { await action(); setFeedback({ tone: "success", text: successText }); await remote.reload(); }
    catch (failure) { setFeedback({ tone: "danger", text: failure instanceof Error ? failure.message : "操作失败，请重试" }); }
    finally { setBusyAction(false); }
  }
  async function createAccount(payload: { tenantId: string; purpose: ServiceAccountPurpose; expiresAt: string | null }) {
    await runAction(async () => {
      const result = await api<CreateAccountResponse>("/api/service-accounts", { method: "POST", body: JSON.stringify(payload) });
      setShowCreateAccount(false);
      setSecretReveal({ title: "服务账号已创建", description: "系统已生成密钥。它只显示这一次，确认保存前不能关闭。", secret: result.clientSecret, fingerprint: result.fingerprint ?? null });
    }, "服务账号已创建，密钥已签发");
  }
  async function revokeAccount(id: string, reason: string) {
    await runAction(async () => { await api(`/api/service-accounts/${id}/revoke`, { method: "POST", body: JSON.stringify({ reason }) }); }, "服务账号已停用");
  }

  return <div className="page runtime-page"><PageHeader
    icon={Cpu}
    title="运行授权与商业计量"
    meta={<span>管理运行身份、接口配额、计量规则与打印安全策略</span>}
    actions={<button className="button secondary" onClick={() => void remote.reload()}><RefreshCw size={15} />刷新</button>}
    navigation={<V3PageTabs tabs={runtimeTabs} active={activeTab} onChange={(value) => setActiveTab(value as RuntimeTab)} ariaLabel="运行授权与商业计量页内视图" />}
  />
    <FeedbackBar feedback={feedback} />
    <RemotePanel state={remote.state} onRetry={remote.reload}>{(data) => <div className="workspace-stack">
      <div id="runtime-panel-accounts" role="tabpanel" aria-labelledby="v3-page-tab-accounts" className="runtime-tab-panel" hidden={activeTab !== "accounts"}>
        {data.accounts !== null && <ServiceAccountsLedger accounts={data.accounts} tenants={data.tenants} busy={busyAction} canCreate={caps.showCreateAccount} canRotate={caps.showRotateAccount} canRevoke={caps.showRevokeAccount} onCreate={() => setShowCreateAccount(true)} onRotate={setRotationAccount} onRevoke={revokeAccount} />}
      </div>
      <div id="runtime-panel-metering" role="tabpanel" aria-labelledby="v3-page-tab-metering" className="runtime-tab-panel workspace-stack" hidden={activeTab !== "metering"}>
        <div className="workspace-grid">{(data.planCatalog !== null || data.subscriptions !== null) && <section className="panel"><div className="section-title"><div><PackageCheck size={18} /><h3>套餐与订阅</h3></div><div className="inline-actions"><button className="button secondary" type="button" onClick={() => appNavigate(data.planCatalog !== null ? "/plans" : "/subscriptions")}>{data.planCatalog !== null ? "前往套餐目录" : "前往订阅中心"}</button></div></div>{data.subscriptions !== null && <table className="compact-table"><thead><tr><th>企业</th><th>套餐</th><th>状态</th></tr></thead><tbody>{data.subscriptions.map((item) => <tr key={item.id}><td>{item.tenantName}</td><td>{item.planName}</td><td><StatusBadge value={item.status} /></td></tr>)}</tbody></table>}{data.planCatalog !== null && <small className="panel-footnote">套餐配置统一在「套餐目录」维护。当前已发布套餐版本 <span className="app-number">{data.planCatalog.flatMap((plan) => plan.versions).filter((version) => version.status === "published").length}</span> 个。</small>}{data.subscriptions !== null && <small className="panel-footnote">正式订阅调整请在「订阅中心」发起变更。</small>}</section>}
        <section className="panel"><div className="section-title"><div><Gauge size={18} /><h3>计量规则</h3></div><div className="inline-actions"><button className="button secondary" type="button" onClick={() => appNavigate("/usage")}>{caps.showCreateMeter ? "维护计量规则" : "查看用量与额度"}</button></div></div><div className="metric-lines">{data.meters.map((meter) => <div key={meter.id}><span>{meter.displayName}<small>{meter.unit} · {meter.aggregation === "max" ? "取最高值" : meter.aggregation === "last" ? "取最新值" : meter.aggregation === "unique_count" ? "去重计数" : "累计相加"}</small></span><StatusBadge value={meter.lifecycle ?? "active"} /></div>)}{!data.meters.length && <span className="empty-inline">暂无计量规则</span>}</div></section></div>
        {data.quotaPolicy !== null && <QuotaPolicyPanel policy={data.quotaPolicy} canManage={caps.showSaveQuota} onSaved={remote.reload} />}
      </div>
      <div id="runtime-panel-print" role="tabpanel" aria-labelledby="v3-page-tab-print" className="runtime-tab-panel" hidden={activeTab !== "print"}>
        {caps.loadPrintPolicy && <PrintAgentPolicyPanel policy={data.printAgentPolicy} canManage={caps.showSavePrintPolicy} onSaved={remote.reload} />}
      </div>
    </div>}</RemotePanel>
    {showCreateAccount && <ServiceAccountWizard tenants={remote.state.status === "ready" ? remote.state.data.tenants : []} submitting={busyAction} onClose={() => setShowCreateAccount(false)} onSubmit={createAccount} />}
    {rotationAccount && <RotationDialog account={rotationAccount} onClose={() => setRotationAccount(null)} onReload={remote.reload} onRevealSecret={setSecretReveal} />}
    {secretReveal && <SecretReveal reveal={secretReveal} onClose={() => setSecretReveal(null)} />}
  </div>;
}

function ServiceAccountsLedger({ accounts, tenants, busy, canCreate, canRotate, canRevoke, onCreate, onRotate, onRevoke }: { accounts: Account[]; tenants: Tenant[]; busy: boolean; canCreate: boolean; canRotate: boolean; canRevoke: boolean; onCreate: () => void; onRotate: (account: Account) => void; onRevoke: (id: string, reason: string) => Promise<void> }) {
  const tenantNameOf = useMemo(() => new Map(tenants.map((tenant) => [tenant.id, tenant.name])), [tenants]);
  const columns: Array<DataTableColumn<Account>> = [
    { key: "tenant", header: "企业", render: (account) => <span>{account.tenantName ?? tenantNameOf.get(account.tenantId) ?? "企业名称待同步"}<small>{purposeLabel(account.purpose)}</small></span>, width: "17%" },
    { key: "purpose", header: "用途", render: (account) => purposeLabel(account.purpose), width: "13%" },
    { key: "status", header: "状态", render: (account) => <StatusBadge value={account.status} />, width: "11%" },
    { key: "version", header: "版本与指纹", render: (account) => <span>第 <span className="app-number">{account.activeVersion ?? 1}</span> 版<small>{account.fingerprint ? `指纹 ${shortFingerprint(account.fingerprint)}` : "指纹待签发"}</small></span>, width: "16%" },
    { key: "lastUsed", header: "最近使用", render: (account) => <span>{formatDate(account.lastUsedAt, "从未使用")}<small>{account.lastSource ? businessLabel(sourceLabels, account.lastSource, "来源待确认") : "来源待确认"}</small></span>, width: "15%" },
    { key: "failed", header: "失败次数", render: (account) => <span className="app-number">{account.failedAuthCount ?? 0}</span>, align: "right", width: "9%" },
    { key: "rotated", header: "轮换日", render: (account) => <span className="app-number">{formatDate(account.lastRotatedAt, "尚未轮换")}</span>, width: "12%" },
    { key: "risk", header: "风险", render: (account) => riskText(account.risk), width: "12%" },
  ];
  return <SectionCard icon={KeyRound} title="服务账号台账" meta={<span><strong className="app-number">{accounts.length}</strong> 个账号</span>} variant="ledger" action={canCreate ? <button className="button primary" disabled={busy} onClick={onCreate}><Plus size={15} />新增服务账号</button> : undefined}>
    <DataTable columns={columns} rows={accounts} rowKey={(account) => account.id} ariaLabel="服务账号台账" empty="暂无服务账号。需要 ERP、打印组件或连接检查接入时，从右上角新增。" rowActions={(account) => <div className="inline-actions">{canRotate && <button className="button secondary" disabled={account.status !== "active" || busy} onClick={() => onRotate(account)}><RotateCw size={14} />轮换</button>}{canRevoke && <RevokeAccountButton disabled={account.status !== "active" || busy} onRevoke={(reason) => onRevoke(account.id, reason)} />}</div>} />
  </SectionCard>;
}

function ServiceAccountWizard({ tenants, submitting, onClose, onSubmit }: { tenants: Tenant[]; submitting: boolean; onClose: () => void; onSubmit: (payload: { tenantId: string; purpose: ServiceAccountPurpose; expiresAt: string | null }) => Promise<void> }) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? "");
  const [purpose, setPurpose] = useState<ServiceAccountPurpose>("erp_runtime");
  const [expiresChoice, setExpiresChoice] = useState("90");
  const [error, setError] = useState<string | null>(null);
  const expiresAt = expiryFromChoice(expiresChoice);
  const selectedTenant = tenants.find((tenant) => tenant.id === tenantId);
  const steps = [
    { key: "tenant", title: "选择企业", summary: "先选这个机器账号代表哪一家企业访问运行时接口。", content: <div className="form-grid"><div className="field-block full">企业<V3Select value={tenantId} onChange={setTenantId} options={tenants.map((tenant) => ({ value: tenant.id, label: tenant.name }))} ariaLabel="选择企业" placeholder="请选择企业" /></div></div>, validate: () => tenantId ? null : "请选择一家企业。" },
    { key: "purpose", title: "选择用途", summary: "只选择用途，不填写账号名、密钥或权限。系统会按用途生成最小权限。", content: <div className="form-grid"><div className="field-block full">用途<V3Select value={purpose} onChange={(next) => setPurpose(next as ServiceAccountPurpose)} options={serviceAccountPurposes.map((item) => ({ value: item, label: serviceAccountPurposePresets[item].label, hint: purposeHint(item) }))} ariaLabel="选择服务账号用途" /></div></div>, validate: () => serviceAccountPurposes.includes(purpose) ? null : "请选择服务账号用途。" },
    { key: "expiry", title: "确认有效期", summary: "建议给机器账号设置到期日，到期前再按实际接入情况续换。", content: <div className="form-grid"><div className="field-block full">有效期<V3Select value={expiresChoice} onChange={setExpiresChoice} options={expiryOptions} ariaLabel="选择服务账号有效期" /></div><div className="quota-platform-card full"><strong>创建前确认</strong><small>企业：{selectedTenant?.name ?? "待选择"} · 用途：{serviceAccountPurposePresets[purpose].label} · {expiresAt ? `到期日 ${formatDate(expiresAt, "")}` : "长期有效"}</small></div></div>, validate: () => tenantId ? null : "请选择一家企业。" },
  ];
  async function submit() {
    setError(null);
    try { await onSubmit({ tenantId, purpose, expiresAt }); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "创建失败，请重试"); }
  }
  return <Wizard title="新增服务账号" description="按企业和用途签发最小权限账号，密钥由系统生成且只展示一次。" steps={steps} submitLabel="创建并签发密钥" onSubmit={() => void submit()} onClose={onClose} submitting={submitting} submitError={error} submitDisabled={!tenantId} />;
}

function RotationDialog({ account, onClose, onReload, onRevealSecret }: { account: Account; onClose: () => void; onReload: () => Promise<void>; onRevealSecret: (reveal: SecretRevealState) => void }) {
  const [reason, setReason] = useState("例行轮换运行服务账号密钥");
  const [startKey] = useState(() => makeIdempotencyKey("rotate-start"));
  const [activateKey] = useState(() => makeIdempotencyKey("rotate-activate"));
  const [revokeKey] = useState(() => makeIdempotencyKey("revoke-old-version"));
  const [pendingVersion, setPendingVersion] = useState<number | null>(null);
  const [pendingFingerprint, setPendingFingerprint] = useState<string | null>(null);
  const [verificationSecret, setVerificationSecret] = useState("");
  const [status, setStatus] = useState<"idle" | "starting" | "secret_saved" | "verifying" | "verified" | "activating" | "activated" | "revoking" | "old_revoked">("idle");
  const [error, setError] = useState<string | null>(null);
  const oldVersion = account.activeVersion ?? null;
  async function startRotation() {
    setError(null);
    if (reason.trim().length < 8) { setError("请填写至少 8 个字的轮换原因。"); return; }
    setStatus("starting");
    try {
      const result = await api<RotationStartResponse>(`/api/service-accounts/${account.id}/rotation/start`, { method: "POST", body: JSON.stringify({ reason, idempotencyKey: startKey }) });
      setPendingVersion(result.version);
      setPendingFingerprint(result.fingerprint ?? null);
      if (!result.clientSecret) {
        setStatus("idle");
        setError("该轮换请求已经签发过新密钥，系统不会再次展示密钥正文。请使用已安全保存的密钥继续；如未保存，请先吊销待处理版本后重新发起轮换。");
        return;
      }
      onRevealSecret({ title: "新版本密钥已签发", description: "请先保存到企业服务器或密钥保险库。关闭前必须确认已经保存。", secret: result.clientSecret, fingerprint: result.fingerprint ?? null, onSaved: () => setStatus("secret_saved") });
    } catch (failure) {
      setStatus("idle");
      setError(failure instanceof Error ? failure.message : "签发新版本失败，请重试。");
    }
  }
  async function verifyRotation() {
    if (!pendingVersion) return;
    setError(null); setStatus("verifying");
    try {
      await api(`/api/service-accounts/${account.id}/rotation/verify`, { method: "POST", body: JSON.stringify({ version: pendingVersion, clientSecret: verificationSecret.trim() }) });
      setVerificationSecret("");
      setStatus("verified");
    } catch (failure) {
      setStatus("secret_saved");
      setError(failure instanceof Error ? failure.message : "新密钥验证失败，请确认企业侧已经使用刚保存的密钥。");
    }
  }
  async function activateRotation() {
    if (!pendingVersion) return;
    setError(null); setStatus("activating");
    try {
      await api(`/api/service-accounts/${account.id}/rotation/activate`, { method: "POST", body: JSON.stringify({ version: pendingVersion, reason, idempotencyKey: activateKey }) });
      setStatus("activated");
      await onReload();
    } catch (failure) {
      setStatus("verified");
      setError(failure instanceof Error ? failure.message : "激活新版本失败，当前账号未完成轮换。");
    }
  }
  async function revokeOldVersion() {
    if (!oldVersion) return;
    setError(null); setStatus("revoking");
    try {
      await api(`/api/service-accounts/${account.id}/secret-versions/${oldVersion}/revoke`, { method: "POST", body: JSON.stringify({ version: oldVersion, reason, idempotencyKey: revokeKey }) });
      setStatus("old_revoked");
      await onReload();
    } catch (failure) {
      setStatus("activated");
      setError(failure instanceof Error ? failure.message : "旧版本停用失败，请稍后重试。");
    }
  }
  return <Dialog title="轮换服务账号密钥" description="按步骤签发、保存、验证并激活新版本；只有服务端确认后才显示完成。" onClose={onClose} wide>
    <div className="form-grid">
      <label className="full">轮换原因<textarea rows={3} value={reason} disabled={status !== "idle"} onChange={(event) => setReason(event.target.value)} placeholder="例如：例行季度轮换，企业侧已安排同步窗口" /></label>
      <div className="quota-platform-card full"><strong>{purposeLabel(account.purpose)} · 当前第 <span className="app-number">{account.activeVersion ?? 1}</span> 版</strong><small>{account.fingerprint ? `当前指纹 ${shortFingerprint(account.fingerprint)}` : "当前指纹待同步"}</small></div>
      <RotationSteps status={status} pendingVersion={pendingVersion} pendingFingerprint={pendingFingerprint} />
      {status === "secret_saved" && <label className="full">粘贴刚保存的新密钥用于验证<input type="password" autoComplete="off" value={verificationSecret} onChange={(event) => setVerificationSecret(event.target.value)} placeholder="只用于本次验证，页面不会保存" /></label>}
      {error && <div className="form-error full">{error}</div>}
      <div className="form-actions full">
        <button type="button" className="button secondary" onClick={onClose}>关闭</button>
        {status === "idle" && <button type="button" className="button primary" onClick={() => void startRotation()}>签发新版本</button>}
        {status === "starting" && <button type="button" className="button primary" disabled>正在签发…</button>}
        {status === "secret_saved" && <button type="button" className="button primary" disabled={verificationSecret.trim().length < 24} onClick={() => void verifyRotation()}>验证新密钥</button>}
        {status === "verifying" && <button type="button" className="button primary" disabled>正在验证…</button>}
        {status === "verified" && <button type="button" className="button primary" onClick={() => void activateRotation()}>激活新版本</button>}
        {status === "activating" && <button type="button" className="button primary" disabled>正在激活…</button>}
        {status === "activated" && oldVersion !== null && oldVersion !== pendingVersion && <button type="button" className="button danger" onClick={() => void revokeOldVersion()}><Trash2 size={14} />停用旧版本</button>}
        {status === "revoking" && <button type="button" className="button danger" disabled>正在停用旧版本…</button>}
        {(status === "activated" || status === "old_revoked") && <button type="button" className="button primary" onClick={onClose}>完成</button>}
      </div>
    </div>
  </Dialog>;
}

function RotationSteps({ status, pendingVersion, pendingFingerprint }: { status: string; pendingVersion: number | null; pendingFingerprint: string | null }) {
  const steps = [
    { key: "start", label: "签发新版本", done: ["secret_saved", "verifying", "verified", "activating", "activated", "revoking", "old_revoked"].includes(status), active: ["idle", "starting"].includes(status) },
    { key: "save", label: "保存一次密钥", done: ["secret_saved", "verifying", "verified", "activating", "activated", "revoking", "old_revoked"].includes(status), active: status === "secret_saved" },
    { key: "verify", label: "验证企业侧可用", done: ["verified", "activating", "activated", "revoking", "old_revoked"].includes(status), active: ["verifying", "verified"].includes(status) },
    { key: "activate", label: "激活新版本", done: ["activated", "revoking", "old_revoked"].includes(status), active: ["activating", "activated"].includes(status) },
  ];
  return <div className="metric-lines full">{steps.map((step) => <div key={step.key}><span>{step.label}<small>{step.key === "start" && pendingVersion ? `待激活第 ${pendingVersion} 版${pendingFingerprint ? ` · 指纹 ${shortFingerprint(pendingFingerprint)}` : ""}` : step.done ? "已完成" : step.active ? "正在处理" : "等待上一步"}</small></span><StatusBadge value={step.done ? "completed" : step.active ? "processing" : "pending"} /></div>)}</div>;
}

function RevokeAccountButton({ disabled, onRevoke }: { disabled: boolean; onRevoke: (reason: string) => Promise<void> }) {
  const [open, setOpen] = useState(false); const [reason, setReason] = useState(""); const [error, setError] = useState("");
  if (!open) return <button className="button ghost danger" disabled={disabled} onClick={() => setOpen(true)}><Trash2 size={14} />停用</button>;
  return <Dialog title="停用服务账号" description="停用后该账号无法再访问运行时接口，请确认企业侧已切换。" onClose={() => setOpen(false)}><div className="form-grid"><label className="full">停用原因<textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="至少 8 个字符" /></label>{error && <div className="form-error full">{error}</div>}<div className="form-actions full"><button className="button secondary" onClick={() => setOpen(false)}>取消</button><button className="button danger" onClick={() => { if (reason.trim().length < 8) { setError("原因至少 8 个字符"); return; } void onRevoke(reason).then(() => setOpen(false)); }}>确认停用</button></div></div></Dialog>;
}

function SecretReveal({ reveal, onClose }: { reveal: NonNullable<SecretRevealState>; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState("");
  async function copy() { try { await navigator.clipboard.writeText(reveal.secret); setCopied(true); } catch { setCopied(false); } }
  function requestClose() {
    if (!acknowledged) { setError("请先确认已经保存密钥。关闭后无法再次查看。"); return; }
    reveal.onSaved?.();
    onClose();
  }
  return <Dialog title={reveal.title} description={reveal.description} onClose={requestClose}>
    <div className="form-grid">
      <label className="full">本次生成的密钥<code className="secret-box">{reveal.secret}</code></label>
      {reveal.fingerprint && <small className="full">指纹：{shortFingerprint(reveal.fingerprint)}</small>}
      <label className="full"><input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />我已把密钥保存到企业服务器或密钥保险库，知道关闭后不能再查看。</label>
      {error && <div className="form-error full">{error}</div>}
      <div className="form-actions full"><button className="button secondary" onClick={() => void copy()}>{copied ? "已复制" : "复制密钥"}</button><button className="button primary" disabled={!acknowledged} onClick={requestClose}>确认已保存</button></div>
    </div>
  </Dialog>;
}

const quotaPlatformLabels: Record<string, string> = { wechat_video: "微信小店", douyin: "抖音", kuaishou: "快手", taobao: "淘宝", tmall: "天猫", jd: "京东", pdd: "拼多多", xiaohongshu: "小红书", alibaba: "阿里巴巴" };
const quotaPlatformLogos: Record<string, string> = {
  wechat_video: "/platform/wechat-shop.png",
  douyin: "/platform/douyin-shop.png",
  kuaishou: "/platform/kuaishou.png",
  taobao: "/platform/taobao.png",
  tmall: "/platform/tmall.png",
  pdd: "/platform/pdd.png",
  xiaohongshu: "/platform/xiaohongshu.png",
};
type QuotaPlatformValues = { qpsLimit: number; dailyCallBudget: number; warningThreshold: number; backoffCapMs: number };

function thresholdOptions(current: number) {
  const presets = [0.5, 0.6, 0.7, 0.8, 0.9, 1];
  const values = presets.includes(current) ? presets : [...presets, current].sort((a, b) => a - b);
  return values.map((value) => ({ value: String(value), label: `${Math.round(value * 100)}%`, ...(value === 0.8 ? { hint: "推荐" } : {}) }));
}

function backoffOptions(current: number) {
  const presets = [300000, 600000, 900000, 1800000];
  const values = presets.includes(current) ? presets : [...presets, current].sort((a, b) => a - b);
  return values.map((value) => ({ value: String(value), label: value >= 60000 ? `${value / 60000} 分钟` : `${value / 1000} 秒`, ...(value === 900000 ? { hint: "推荐" } : {}) }));
}

function QuotaPolicyPanel({ policy, canManage, onSaved }: { policy: QuotaPolicy; canManage: boolean; onSaved: () => Promise<void> }) {
  const [platforms, setPlatforms] = useState<Record<string, QuotaPlatformValues>>({});
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  useEffect(() => {
    const fallback: Record<string, QuotaPlatformValues> = {
      wechat_video: { qpsLimit: 10, dailyCallBudget: 50000, warningThreshold: 0.8, backoffCapMs: 900000 },
      douyin: { qpsLimit: 10, dailyCallBudget: 0, warningThreshold: 0.9, backoffCapMs: 900000 },
      kuaishou: { qpsLimit: 5, dailyCallBudget: 0, warningThreshold: 0.9, backoffCapMs: 900000 },
      taobao: { qpsLimit: 8, dailyCallBudget: 80000, warningThreshold: 0.8, backoffCapMs: 900000 },
      tmall: { qpsLimit: 8, dailyCallBudget: 80000, warningThreshold: 0.8, backoffCapMs: 900000 },
      jd: { qpsLimit: 10, dailyCallBudget: 50000, warningThreshold: 0.8, backoffCapMs: 900000 },
      pdd: { qpsLimit: 10, dailyCallBudget: 0, warningThreshold: 0.9, backoffCapMs: 900000 },
      xiaohongshu: { qpsLimit: 5, dailyCallBudget: 0, warningThreshold: 0.9, backoffCapMs: 900000 },
      alibaba: { qpsLimit: 8, dailyCallBudget: 0, warningThreshold: 0.9, backoffCapMs: 900000 },
    };
    setPlatforms(policy.platforms && Object.keys(policy.platforms).length ? policy.platforms : fallback);
  }, [policy]);
  function update(key: string, patch: Partial<QuotaPlatformValues>) {
    setPlatforms((current) => {
      const existing = current[key];
      if (!existing) return current;
      return { ...current, [key]: { ...existing, ...patch } as QuotaPlatformValues };
    });
  }
  async function save() {
    setBusy(true); setFeedback(null);
    try {
      await api("/api/quota-policy", { method: "PUT", body: JSON.stringify({ platforms }) });
      setFeedback({ tone: "success", text: "配额策略已保存并下发" });
      await onSaved();
    } catch (value) { setFeedback({ tone: "danger", text: value instanceof Error ? value.message : "保存失败" }); }
    finally { setBusy(false); }
  }
  return <section className="panel"><div className="section-title"><div><SlidersHorizontal size={18} /><h3>平台接口配额策略</h3></div><StatusBadge value={policy.id ? "configured" : "defaults"} /></div>
    <p className="panel-footnote">这里控制各平台每天最多调用多少次、每秒最多调用多少次、用量到多少开始提醒、遇到限流时最长暂停多久。未配置时系统使用内置默认值；每日可用次数填 0 表示不限制每天总量。</p>
    <FeedbackBar feedback={feedback} />
    <div className="quota-platform-list">
      {Object.entries(platforms).map(([key, value]) => <div key={key} className="quota-platform-card">
        <div className="quota-platform-heading">
          <span className="quota-platform-logo">
            {quotaPlatformLogos[key]
              ? <img src={quotaPlatformLogos[key]} alt="" />
              : <Store size={18} aria-hidden />}
          </span>
          <strong>{businessLabel(quotaPlatformLabels, key, "平台配额项待确认")}</strong>
        </div>
        <div className="quota-platform-fields">
          <label>每秒调用上限<input type="number" min={1} step={1} value={value.qpsLimit} disabled={!canManage} onChange={(event) => update(key, { qpsLimit: Number(event.target.value) })} /></label>
          <label>每日可用次数<input type="number" min={0} step={1000} value={value.dailyCallBudget} disabled={!canManage} onChange={(event) => update(key, { dailyCallBudget: Number(event.target.value) })} /></label>
          <label>用量提醒点<V3Select value={String(value.warningThreshold)} onChange={(next) => update(key, { warningThreshold: Number(next) })} options={thresholdOptions(value.warningThreshold)} ariaLabel={`${businessLabel(quotaPlatformLabels, key, "平台配额项待确认")}用量提醒点`} disabled={!canManage} /></label>
          <label>限流后最长等待<V3Select value={String(value.backoffCapMs)} onChange={(next) => update(key, { backoffCapMs: Number(next) })} options={backoffOptions(value.backoffCapMs)} ariaLabel={`${businessLabel(quotaPlatformLabels, key, "平台配额项待确认")}限流后最长等待`} disabled={!canManage} /></label>
        </div>
      </div>)}
    </div>
    {canManage && <div className="form-actions"><button className="button primary" disabled={busy} onClick={() => void save()}>{busy ? "正在保存…" : "保存并下发"}</button></div>}
  </section>;
}

const productionPrintOrigin = "https://erp.ekaiwork.com";
const defaultPrintOrigins = [productionPrintOrigin, "http://localhost:3000", "http://127.0.0.1:3000", "http://localhost:5173", "http://127.0.0.1:5173"];

function PrintAgentPolicyPanel({ policy, canManage, onSaved }: { policy: PrintAgentPolicy | null; canManage: boolean; onSaved: () => Promise<void> }) {
  const [origins, setOrigins] = useState<string[]>(defaultPrintOrigins);
  const [newOrigin, setNewOrigin] = useState("");
  const [originError, setOriginError] = useState("");
  const [probeState, setProbeState] = useState<OriginProbeState | null>(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const originsKey = origins.join("\n");
  const currentProbe = probeState?.originsKey === originsKey ? probeState : null;
  const probeGate = evaluatePrintOriginProbe(origins, currentProbe);
  const rows = useMemo<OriginRow[]>(() => {
    const results = new Map((currentProbe?.results ?? []).map((item) => [item.origin, item]));
    return origins.map((origin) => ({ origin, required: origin === productionPrintOrigin, developmentOnly: isLocalPrintOrigin(origin), probe: results.get(origin) ?? null }));
  }, [currentProbe, origins]);
  const columns: Array<DataTableColumn<OriginRow>> = [
    { key: "site", header: "网站域名", render: (row) => <span className="print-origin-cell"><strong>{row.origin}</strong><small>{row.required ? "正式 ERP，必须保留" : row.developmentOnly ? "localhost 仅开发调试" : "正式网站需验证可访问"}</small></span>, width: "31%" },
    { key: "status", header: "验证状态", render: (row) => <ProbeStatusPill result={row.probe} />, width: "17%" },
    { key: "http", header: "HTTP 状态", render: (row) => <span className="app-number">{probeHttpText(row.probe)}</span>, width: "14%" },
    { key: "checked", header: "证据时间", render: (row) => <span className="app-number">{formatDate(row.probe?.checkedAt, "待验证")}</span>, width: "16%" },
    { key: "advice", header: "建议", render: (row) => <span className="print-origin-advice">{printOriginAdvice(row)}</span> },
  ];

  useEffect(() => {
    setOrigins(normalizePrintOriginList(policy?.allowedOrigins ?? defaultPrintOrigins));
    setNewOrigin("");
    setOriginError("");
    setProbeState(null);
  }, [policy]);

  function addOrigin() {
    const normalized = normalizePrintOriginInput(newOrigin);
    if (!normalized.origin) {
      setOriginError(normalized.error);
      return;
    }
    if (origins.includes(normalized.origin)) {
      setOriginError("这个网站已经在列表里。");
      return;
    }
    setOrigins((current) => normalizePrintOriginList([...current, normalized.origin]));
    setNewOrigin("");
    setOriginError("");
    setProbeState(null);
    setFeedback(null);
  }

  function removeOrigin(origin: string) {
    if (origin === productionPrintOrigin) return;
    setOrigins((current) => normalizePrintOriginList(current.filter((item) => item !== origin)));
    setProbeState(null);
    setFeedback(null);
  }

  async function probe() {
    setBusy(true); setFeedback(null); setOriginError("");
    try {
      const result = await api<OriginProbeResponse>("/api/print-agent-origin-policy/probe", { method: "POST", body: JSON.stringify({ allowedOrigins: origins, idempotencyKey: makeIdempotencyKey("print-origin-probe") }) });
      const nextProbe = { ...result, originsKey } satisfies OriginProbeState;
      setProbeState(nextProbe);
      const nextGate = evaluatePrintOriginProbe(origins, nextProbe);
      setFeedback(nextGate.ok ? { tone: "success", text: "网站连通性验证通过，可以保存并签发。" } : { tone: "danger", text: nextGate.message });
    } catch (value) { setFeedback({ tone: "danger", text: value instanceof Error ? value.message : "验证失败，请稍后重试。" }); }
    finally { setBusy(false); }
  }

  async function save() {
    if (!probeGate.ok) {
      setFeedback({ tone: "danger", text: probeGate.message });
      return;
    }
    setBusy(true); setFeedback(null);
    try {
      await api("/api/print-agent-origin-policy", { method: "PUT", body: JSON.stringify({ allowedOrigins: origins }) });
      setFeedback({ tone: "success", text: "打印组件安全域名已签发新版本" });
      await onSaved();
    } catch (value) { setFeedback({ tone: "danger", text: value instanceof Error ? value.message : "保存失败" }); }
    finally { setBusy(false); }
  }
  return <SectionCard
    icon={ShieldCheck}
    title="打印组件安全域名"
    className="print-origin-card"
    meta={<><StatusBadge value={policy ? "active" : "not_configured"} /><span><strong className="app-number">{origins.length}</strong> 个网站</span></>}
    variant="ledger"
    filters={canManage ? <div className="print-origin-toolbar"><label className="print-origin-input"><span>新增网站域名</span><input aria-label="新增网站域名" value={newOrigin} disabled={busy} onChange={(event) => { setNewOrigin(event.target.value); setOriginError(""); }} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addOrigin(); } }} placeholder="新增网站域名，例如：https://erp.ekaiwork.com" /></label><button type="button" className="button secondary" disabled={busy || !newOrigin.trim()} onClick={addOrigin}><Plus size={14} />添加</button><button type="button" className="button secondary" disabled={busy || origins.length === 0} onClick={() => void probe()}><RefreshCw size={14} />{busy ? "验证中…" : "验证网站连通性"}</button></div> : undefined}
    action={canManage ? <button type="button" className="button primary" disabled={busy || !probeGate.ok} onClick={() => void save()}>{busy ? "正在处理…" : "保存并签发新版本"}</button> : undefined}
  >
    <div className="print-origin-summary">
      <span>生产 ERP 域名会自动保留。</span>
      <span>输入完整网址或域名后，系统会自动整理成网站根地址。</span>
      <span>localhost 仅用于本机开发，不能作为生产连通依据。</span>
      {policy && <span>当前策略第 <strong className="app-number">{policy.policyVersion}</strong> 版 · {formatDate(policy.updatedAt, "更新时间待确认")}</span>}
    </div>
    <FeedbackBar feedback={feedback} />
    {originError && <div className="form-error print-origin-error">{originError}</div>}
    {canManage ? (
      <DataTable columns={columns} rows={rows} rowKey={(row) => row.origin} ariaLabel="打印组件安全域名验证结果" empty="暂无网站域名。生产 ERP 域名会自动保留。" rowActions={(row) => <button type="button" className="button ghost danger" disabled={busy || row.required} onClick={() => removeOrigin(row.origin)}><Trash2 size={14} />删除</button>} />
    ) : (
      <DataTable columns={columns} rows={rows} rowKey={(row) => row.origin} ariaLabel="打印组件安全域名验证结果" empty="暂无网站域名。生产 ERP 域名会自动保留。" />
    )}
  </SectionCard>;
}

function normalizePrintOriginInput(value: string): { origin: string; error: "" } | { origin: ""; error: string } {
  const trimmed = value.trim();
  if (!trimmed) return { origin: "", error: "请先输入一个网站域名。" };
  const hasScheme = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed);
  const localLike = /^(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/i.test(trimmed);
  const candidate = hasScheme ? trimmed : `${localLike ? "http" : "https"}://${trimmed}`;
  try {
    const url = new URL(candidate);
    const origin = url.origin;
    const local = isLocalPrintOrigin(origin);
    if (!["http:", "https:"].includes(url.protocol)) return { origin: "", error: "只支持 http 或 https 网站地址。" };
    if (url.username || url.password) return { origin: "", error: "网站地址不能包含用户名或密码。" };
    if (url.protocol === "http:" && !local) return { origin: "", error: "正式网站必须使用 https。" };
    return { origin, error: "" };
  } catch {
    return { origin: "", error: "网站地址格式不对，请检查后再添加。" };
  }
}

function normalizePrintOriginList(values: string[]) {
  const normalized = new Set<string>([productionPrintOrigin]);
  for (const value of values) {
    const result = normalizePrintOriginInput(value);
    if (result.origin) normalized.add(result.origin);
  }
  return Array.from(normalized);
}

function isLocalPrintOrigin(origin: string) {
  try {
    const hostname = new URL(origin).hostname;
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

function evaluatePrintOriginProbe(origins: string[], probe: OriginProbeState | null): { ok: boolean; message: string } {
  if (!origins.includes(productionPrintOrigin)) return { ok: false, message: "生产 ERP 域名必须保留。" };
  if (!probe) return { ok: false, message: "请先验证网站连通性，通过后再保存。" };
  if (!probe.allRequiredReachable) return { ok: false, message: "还有正式网站没有验证通过，请处理后重新验证。" };
  const results = new Map(probe.results.map((item) => [item.origin, item]));
  for (const origin of origins) {
    const result = results.get(origin);
    if (!result) return { ok: false, message: "验证结果不完整，请重新验证。" };
    if (isLocalPrintOrigin(origin)) {
      if (result.status !== "development_only") return { ok: false, message: "localhost 只能作为开发地址保留，请重新验证。" };
    } else if (result.status !== "reachable") {
      return { ok: false, message: "还有正式网站没有验证通过，请处理后重新验证。" };
    }
  }
  return { ok: true, message: "" };
}

function ProbeStatusPill({ result }: { result: OriginProbeResult | null }) {
  if (!result) return <span className="probe-pill neutral"><i />待验证</span>;
  if (result.status === "reachable") return <span className="probe-pill success"><i />验证通过</span>;
  if (result.status === "development_only") return <span className="probe-pill warning"><i />仅开发可用</span>;
  return <span className="probe-pill danger"><i />验证失败</span>;
}

function probeHttpText(result: OriginProbeResult | null) {
  if (!result) return "待验证";
  if (typeof result.httpStatus === "number") return `HTTP ${result.httpStatus}`;
  if (result.status === "development_only") return "本机开发";
  return "无响应";
}

function printOriginAdvice(row: OriginRow) {
  if (!row.probe) return row.developmentOnly ? "本机开发地址也要验证后才能随策略保存。" : "先点击验证网站连通性，拿到真实结果后再保存。";
  if (row.probe.status === "reachable") return row.required ? "正式 ERP 已可访问，请保留本次验证证据。" : "正式网站已可访问，可随本次策略保存。";
  if (row.probe.status === "development_only") return "只用于本机开发调试，生产发布不要依赖它。";
  if (row.probe.httpStatus && row.probe.httpStatus >= 400) return "网站返回异常，请联系部署人员确认入口后重新验证。";
  return "请检查网站是否可访问、证书是否有效，然后重新验证。";
}

const expiryOptions = [
  { value: "30", label: "30 天", hint: "短期接入" },
  { value: "90", label: "90 天", hint: "推荐" },
  { value: "180", label: "180 天" },
  { value: "365", label: "1 年" },
  { value: "never", label: "长期有效", hint: "仅用于无法定期换密的旧系统" },
];

function expiryFromChoice(choice: string) {
  if (choice === "never") return null;
  const days = Number(choice);
  if (!Number.isFinite(days) || days <= 0) return null;
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function purposeLabel(value: Account["purpose"]) {
  if (!value) return "用途待确认";
  return businessLabel(Object.fromEntries(serviceAccountPurposes.map((item) => [item, serviceAccountPurposePresets[item].label])), value, "用途待确认");
}

function purposeHint(value: ServiceAccountPurpose) {
  if (value === "erp_runtime") return "读取策略、上报用量、读取密封配置";
  if (value === "print_agent") return "只读取打印组件安全域名";
  return "只做连接健康检查";
}

function shortFingerprint(value: string) {
  return value.length > 18 ? `${value.slice(0, 10)}...${value.slice(-6)}` : value;
}

function riskText(value: AccountRisk | null | undefined) {
  if (!value) return "暂无风险";
  if (typeof value === "string") return businessLabel(riskLabels, value, "风险待确认");
  return value.summary ?? businessLabel(riskLabels, value.level ?? "", "风险待确认");
}

function formatDate(value: string | null | undefined, empty: string) {
  if (!value) return empty;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "时间待确认";
  return date.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function makeIdempotencyKey(prefix: string) {
  return `${prefix}-${typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}
